/**
 * Move notation.
 *
 * Fully implemented here: coordinate systems (UCI/UCCI and ICCS), which is what
 * the engine layer and internet-server interchange need.
 *
 *   - UCCI / engine coordinate: files a–i, ranks 0–9, no separator, lowercase.
 *     e.g. `h2e2`. This is what Fairy-Stockfish / Pikafish consume and emit.
 *   - ICCS: same coordinates, uppercase, hyphen-separated. e.g. `H2-E2`.
 *
 * Chinese vertical-line notation (中文縱線, e.g. 炮二平五) is a separate, subtler
 * module — see `chinese-notation.ts` — because of the red/black file mirror and
 * the 前/後 disambiguation rules. It is stubbed there, not here.
 */

import { fileOf, rankOf, square, type Move, type Square } from "./types.js";

const FILE_LETTERS = "abcdefghi";

export function squareToCoord(sq: Square): string {
  return `${FILE_LETTERS[fileOf(sq)]}${rankOf(sq)}`;
}

export function coordToSquare(coord: string): Square {
  const file = FILE_LETTERS.indexOf(coord[0]!.toLowerCase());
  const rank = coord.charCodeAt(1) - "0".charCodeAt(0);
  if (file < 0 || file > 8 || rank < 0 || rank > 9) {
    throw new Error(`Invalid coordinate: "${coord}"`);
  }
  return square(file, rank);
}

/** UCCI/engine move string, e.g. `h2e2`. */
export function moveToUci(move: Move): string {
  return squareToCoord(move.from) + squareToCoord(move.to);
}

export function uciToMove(uci: string): Move {
  if (uci.length !== 4) throw new Error(`Invalid UCI move: "${uci}"`);
  return { from: coordToSquare(uci.slice(0, 2)), to: coordToSquare(uci.slice(2, 4)) };
}

/** ICCS move string, e.g. `H2-E2`. */
export function moveToIccs(move: Move): string {
  return `${squareToCoord(move.from).toUpperCase()}-${squareToCoord(move.to).toUpperCase()}`;
}

export function iccsToMove(iccs: string): Move {
  const [from, to] = iccs.split("-");
  if (!from || !to) throw new Error(`Invalid ICCS move: "${iccs}"`);
  return { from: coordToSquare(from), to: coordToSquare(to) };
}
